// Globals
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  changeTopProductPosition,
  deleteTopProducts,
  getTopProductList,
  productsSelector,
  reorderTopProduct,
} from '../../feature';

// Components
import {
  StyledPageWrapper,
  StyledHeaderBlock,
  StyledPageTitle,
  StyledTabsWrap,
  StyledCreateButton,
  StyledContentBlock,
  StyledTitleModal,
  StyledDescription,
  StyledButtonsWrapper,
} from '../ProductOfMonthContainer/ProductOfMonthContainerStyles';
import { Modal, ModalSize, showSuccessToast, useForm } from '@/components';
import { PlusCircleIcon } from '@/icons';
import { Button, Drawer } from '@/ui';
import { TopProductsList } from '../../components';
import AddTopProductContainer from '../AddTopProductContainer/AddTopProductContainer';

// Models
import { ITopProduct } from '@/models/products.model';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { useModal } from '@/hooks';
import { ReorderTopProductsDTO } from '../../dtos';

const TopProductsContainer = () => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const [form] = useForm();
  const [currentProduct, setCurrentProduct] = useState<ITopProduct | null>(null);
  const { isLoading, topProductsList } = useAppState(productsSelector);
  const { canManage } = usePermissions();
  const canManageTopProduct = canManage(Resource.TOP_PRODUCT);
  const { isOpened, openModal, closeModal } = useModal();
  const { isOpened: productFormOpened, openModal: openProductForm, closeModal: closeProductForm } = useModal();

  const changeProductForDelete = (product: ITopProduct) => {
    setCurrentProduct(product);
    openModal();
  };

  const handleEditClick = async (product: ITopProduct) => {
    await form.resetFields();
    setCurrentProduct(product);
    openProductForm();
  };

  const handleDelete = async () => {
    try {
      if (currentProduct) await dispatch(deleteTopProducts(currentProduct.id));
      dispatch(getTopProductList());
      showSuccessToast({ message: t('product_top_deleted') });
      closeModal();
      setCurrentProduct(null);
    } catch {}
  };

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!topProductsList || !over || active.id === over?.id) return;
      const oldIndex = topProductsList.findIndex((m) => m.id === active.id);
      const newIndex = topProductsList.findIndex((m) => m.id === over?.id);
      const newPoints = arrayMove(topProductsList, oldIndex, newIndex).map((m, index) => ({
        ...m,
        position: index + 1,
      }));
      dispatch(changeTopProductPosition(newPoints));

      const dto = new ReorderTopProductsDTO(newPoints);
      try {
        await dispatch(reorderTopProduct(dto));
        showSuccessToast({ message: t('order_changed') });
      } catch {
        dispatch(changeTopProductPosition(topProductsList));
      }
    },
    [topProductsList, dispatch],
  );

  const handleCloseModal = () => {
    closeModal();
    setCurrentProduct(null);
  };

  const handleCloseDrawer = () => {
    closeProductForm();
    setCurrentProduct(null);
    form.resetFields();
  };

  useEffect(() => {
    dispatch(getTopProductList());
  }, []);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('products_top_tab_title')}</StyledPageTitle>
        {canManageTopProduct && (
          <StyledTabsWrap>
            <StyledCreateButton onClick={openProductForm}>
              <PlusCircleIcon />
              {t('products_top_tab_add_button')}
            </StyledCreateButton>
          </StyledTabsWrap>
        )}
      </StyledHeaderBlock>

      <StyledContentBlock>
        <TopProductsList
          isLoading={isLoading}
          productsList={topProductsList}
          handleEditClick={handleEditClick}
          setProductForDelete={changeProductForDelete}
          handleDragEnd={canManageTopProduct ? handleDragEnd : undefined}
          canManageTopProduct={canManageTopProduct}
        />
      </StyledContentBlock>

      <Drawer
        open={productFormOpened}
        onClose={handleCloseDrawer}
        width="29rem"
        destroyOnClose
        title={t('products_top_tab_add_button')}
      >
        <AddTopProductContainer
          currentProduct={currentProduct}
          handleCloseDrawer={handleCloseDrawer}
          formInstance={form}
          isLoading={isLoading}
        />
      </Drawer>
      <Modal visible={isOpened} onCancel={handleCloseModal} size={ModalSize.LARGE}>
        <StyledTitleModal>{t('products_top_delete_title')}</StyledTitleModal>
        <StyledDescription>{t('products_top_delete_description')}</StyledDescription>
        <StyledButtonsWrapper>
          <Button onClick={handleCloseModal} type="primary">
            {t('products_delete_back')}
          </Button>
          <Button onClick={handleDelete} type="text">
            {t('products_delete')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
    </StyledPageWrapper>
  );
};

export default TopProductsContainer;
